/*
 * offer-scents.js
 * Multi-buy offers ("Buy 2 get 1", "Buy 3 get 1") sell several bottles as one
 * variant, but a variant carries only one Scent. This adds a scent picker for
 * each extra bottle and sends every bottle's scent with the cart line as a
 * line item property ("Bottle 1", "Bottle 2", ...), so it shows in the cart,
 * at checkout and on the order.
 *
 * Bottle 1 is the product's own Scent option. Extra bottles follow bottle 1
 * until the shopper picks something else for them.
 *
 * Relies on option-swatches.js having rendered the Scent option as chips
 * (it reads the chip colours from there).
 */
(function () {
  'use strict';

  // Option value that means "several bottles". Total bottles = buy + free.
  var OFFER = /buy\s*(\d+)\s*get\s*(\d+)/i;
  // Option that holds the scent. Compared lower-case.
  var SCENT_OPTIONS = ['scent'];
  var MAX_BOTTLES = 12;

  function legendName(fieldset) {
    var legend = fieldset.querySelector('legend');
    if (!legend) return '';
    // The swatch script appends the selected value to the legend; the option
    // name is the first text node.
    var first = legend.childNodes[0];
    return String((first && first.textContent) || legend.textContent || '').trim().toLowerCase();
  }

  function checkedValue(fieldset) {
    var checked = fieldset.querySelector('input[type="radio"]:checked');
    return checked ? checked.value : '';
  }

  function bottleCount(picker) {
    var count = 0;
    [].forEach.call(picker.querySelectorAll('fieldset'), function (fieldset) {
      var match = OFFER.exec(checkedValue(fieldset));
      if (match) count = parseInt(match[1], 10) + parseInt(match[2], 10);
    });
    return Math.min(count, MAX_BOTTLES);
  }

  function scentFieldset(picker) {
    var sets = picker.querySelectorAll('fieldset');
    for (var i = 0; i < sets.length; i++) {
      if (SCENT_OPTIONS.indexOf(legendName(sets[i])) !== -1) return sets[i];
    }
    return null;
  }

  function productForm(picker) {
    var radio = picker.querySelector('input[type="radio"][form]');
    var id = radio && radio.getAttribute('form');
    return (id && document.getElementById(id)) || null;
  }

  function scentsOf(fieldset) {
    return [].map.call(fieldset.querySelectorAll('input[type="radio"]'), function (input) {
      var chip = input.nextElementSibling;
      var colour = chip && chip.style ? chip.style.getPropertyValue('--gl-sw') : '';
      return { value: input.value, colour: (colour || '').trim() || '#DED8D2' };
    });
  }

  function render(picker, state, count, scents) {
    var block = state.block;
    if (!block) {
      block = document.createElement('div');
      block.className = 'gl-offer';
      block.addEventListener('click', function (event) {
        var chip = event.target.closest('.gl-offer__chip');
        if (!chip) return;
        state.picks[parseInt(chip.dataset.bottle, 10)] = chip.dataset.value;
        sync(picker);
      });
      picker.insertAdjacentElement('afterend', block);
      state.block = block;
    }

    block.textContent = '';

    var intro = document.createElement('p');
    intro.className = 'gl-offer__intro';
    intro.textContent = 'You get ' + count + ' bottles. Bottle 1 is the scent picked above — choose the rest here.';
    block.appendChild(intro);

    for (var bottle = 2; bottle <= count; bottle++) {
      var row = document.createElement('div');
      row.className = 'gl-offer__row';
      row.setAttribute('role', 'group');
      row.setAttribute('aria-label', 'Scent for bottle ' + bottle);

      var head = document.createElement('div');
      head.className = 'gl-offer__head form__label';
      head.textContent = 'Bottle ' + bottle;
      var readout = document.createElement('span');
      readout.className = 'gl-offer__value';
      readout.dataset.bottle = String(bottle);
      head.appendChild(readout);
      row.appendChild(head);

      var list = document.createElement('div');
      list.className = 'gl-offer__list';
      scents.forEach(function (scent) {
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'gl-offer__chip';
        chip.dataset.bottle = String(bottle);
        chip.dataset.value = scent.value;
        chip.setAttribute('aria-pressed', 'false');

        var dot = document.createElement('span');
        dot.className = 'gl-offer__dot';
        dot.style.background = scent.colour;
        dot.setAttribute('aria-hidden', 'true');
        chip.appendChild(dot);

        var name = document.createElement('span');
        name.textContent = scent.value;
        chip.appendChild(name);

        list.appendChild(chip);
      });
      row.appendChild(list);
      block.appendChild(row);
    }
  }

  function clear(picker, state, form) {
    if (state.block) {
      state.block.remove();
      state.block = null;
    }
    if (form) {
      [].forEach.call(form.querySelectorAll('input[data-gl-offer]'), function (input) { input.remove(); });
    }
    state.signature = '';
    state.count = 0;
  }

  function sync(picker) {
    var state = picker._glOffer || (picker._glOffer = { picks: {}, block: null, signature: '', count: 0 });
    var form = productForm(picker);
    var fieldset = scentFieldset(picker);
    var count = fieldset && form ? bottleCount(picker) : 0;

    if (count < 2) {
      if (state.block || state.signature) clear(picker, state, form);
      return;
    }

    var scents = scentsOf(fieldset);
    var values = scents.map(function (scent) { return scent.value; });
    var first = checkedValue(fieldset) || values[0];

    // One scent per bottle. Extra bottles follow bottle 1 until picked.
    var chosen = [first];
    for (var bottle = 2; bottle <= count; bottle++) {
      var pick = state.picks[bottle];
      chosen.push(values.indexOf(pick) !== -1 ? pick : first);
    }

    var signature = count + '|' + values.join(',') + '|' + chosen.join(',');
    var inputs = form.querySelectorAll('input[data-gl-offer]');
    var intact = state.block && state.block.isConnected && inputs.length === count;
    if (signature === state.signature && intact) return;

    if (!state.block || !state.block.isConnected || state.count !== count || state.scentKey !== values.join(',')) {
      if (state.block && !state.block.isConnected) state.block = null;
      render(picker, state, count, scents);
      state.count = count;
      state.scentKey = values.join(',');
    }

    [].forEach.call(state.block.querySelectorAll('.gl-offer__chip'), function (chip) {
      var on = chosen[parseInt(chip.dataset.bottle, 10) - 1] === chip.dataset.value;
      chip.classList.toggle('is-selected', on);
      chip.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    [].forEach.call(state.block.querySelectorAll('.gl-offer__value'), function (readout) {
      readout.textContent = chosen[parseInt(readout.dataset.bottle, 10) - 1];
    });

    [].forEach.call(inputs, function (input) { input.remove(); });
    chosen.forEach(function (value, index) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[Bottle ' + (index + 1) + ']';
      input.value = value;
      input.setAttribute('data-gl-offer', '');
      form.appendChild(input);
    });

    state.signature = signature;
  }

  function scan() {
    var pickers = document.querySelectorAll('variant-radios');
    for (var i = 0; i < pickers.length; i++) {
      var picker = pickers[i];
      if (picker.dataset.glOffer !== 'on') {
        picker.dataset.glOffer = 'on';
        picker.addEventListener('change', (function (target) {
          return function () { setTimeout(function () { sync(target); }, 0); };
        })(picker));
      }
      sync(picker);
    }
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    setTimeout(function () { queued = false; scan(); }, 160);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
  window.addEventListener('load', schedule);
  document.addEventListener('shopify:section:load', schedule);
  if (window.MutationObserver) {
    new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
